import { useCallback, useMemo } from "react";
import {
  generatePath,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useMutation } from "urql";

import { SessionInferenceMode } from "@/components/graphql/schema.graphql";
import { InferenceFormNavigationState } from "@/components/inference-forms/constants";
import { useSnackbar } from "@/components/ui-v2/Snackbar";

import { SessionInferenceFormState } from "../types";
import {
  BasicDataSimpleWorkspaceFragment,
  CreateSession,
  CreateSessionFromTemplate,
} from "./queries.graphql";
import { useUrqlResponseHandler } from "./useUrqlResponseHandler";

interface CreateSessionParams {
  workspace: BasicDataSimpleWorkspaceFragment;
  sessionName: string;
  inferenceFormState: SessionInferenceFormState;
  navigateToNewSession?: boolean;
  templateId?: string;
}

const SEARCH_PARAMS_TO_PRESERVE: string[] = [];

const getNavigationStateFromSearchParams = (
  searchParams: URLSearchParams,
): InferenceFormNavigationState | undefined => {
  const mode = searchParams.get("mode") as SessionInferenceMode;
  const styleId = searchParams.get("styleId");
  if (mode) {
    const state = { mode } as InferenceFormNavigationState;
    if (styleId) {
      state.form = {
        style: {
          id: styleId,
          weight: 1,
        },
      };
    }
    return state;
  }
  return undefined;
};

// Note: we could simply redirect to `session/new` and let the page handle the creation of the session instead of this hook that couple Data + Business + UI layers.
export const useCreateSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { showSuccess } = useSnackbar();

  const navigationState = useMemo<
    InferenceFormNavigationState | undefined
  >(() => {
    if (location.state) {
      return location.state;
    }
    return getNavigationStateFromSearchParams(searchParams);
  }, [location.state, searchParams]);

  const [state, createSession] = useMutation(CreateSession);
  const [, createSessionFromTemplate] = useMutation(CreateSessionFromTemplate);
  const { getResponseFromState } = useUrqlResponseHandler();

  const createSessionAndRedirect = useCallback(
    async ({
      workspace,
      sessionName,
      inferenceFormState,
      navigateToNewSession = true,
      templateId,
    }: CreateSessionParams) => {
      let session;
      if (templateId) {
        const response = await createSessionFromTemplate({
          createSessionFromTemplateInput: {
            templateId,
            workspaceId: workspace.id,
          },
        });
        const { data } = getResponseFromState({
          state: response,
          errorMessage:
            "There was an error creating your session. Please try again.",
        });
        session = data?.session;
      } else {
        const response = await createSession({
          createSessionInput: {
            name: sessionName,
            workspaceId: workspace.id,
            inferenceFormState,
          },
        });
        const { data } = getResponseFromState({
          state: response,
          errorMessage:
            "There was an error creating your session. Please try again.",
        });
        session = data?.session;
      }

      if (session && navigateToNewSession) {
        if (templateId) {
          showSuccess("Template applied: just customize it for your needs.");
        }

        const newSessionId = session.id;
        let newUrl = generatePath(`/${workspace.name}/session/:sessionId`, {
          sessionId: newSessionId,
        });

        const toPreserve = new URLSearchParams();
        searchParams.forEach((value, key) => {
          if (SEARCH_PARAMS_TO_PRESERVE.includes(key)) {
            toPreserve.append(key, value);
          }
        });

        if (toPreserve.size > 0) {
          newUrl += `?${toPreserve.toString()}`;
        }

        navigate(newUrl, {
          replace: true,
          state: navigationState,
        });
      }
      return session;
    },
    [
      createSession,
      createSessionFromTemplate,
      getResponseFromState,
      navigate,
      navigationState,
      searchParams,
      showSuccess,
    ],
  );

  return [state, createSessionAndRedirect] as const;
};
